/**
 * Users service client (families, children, devices, pairing codes).
 */
import { api } from './client';

export interface Family {
  id: string;
  name: string;
  plan: string;
  created_at: string;
}

export type AgeGroup = '5-7' | '8-11' | '12-14' | '15-17';

export interface Child {
  id: string;
  family_id: string;
  name: string;
  nickname: string | null;
  birth_year: number | null;
  color: string | null;
  avatar_url: string | null;
  created_at: string;
  age_group: AgeGroup | null;
}

export interface AgeProfileUi {
  simple_ui: boolean;
  large_buttons: boolean;
  narrated_onboarding: boolean;
  teen_mode: boolean;
}

export interface AgeProfile {
  group: AgeGroup;
  label: string;
  age_min: number;
  age_max: number;
  tone: string;
  summary: string;
  screen_time_minutes: number;
  web_filter_level: 'strict' | 'moderate' | 'light';
  location_transparency: 'always_visible' | 'visible' | 'agreed';
  rewards_focus: boolean;
  weekly_reports: boolean;
  blocked_categories: string[];
  ui: AgeProfileUi;
}

export interface ChildUpdateInput {
  name?: string;
  nickname?: string | null;
  birth_year?: number | null;
  color?: string | null;
}

export interface ChildCreateInput {
  name: string;
  nickname?: string;
  birth_year?: number;
  color?: string;
}

export interface PairingCode {
  code: string;
  expires_at: string;
  deep_link: string;
}

export interface Device {
  id: string;
  child_id: string;
  label: string | null;
  platform: string;
  model: string | null;
  paired_at: string | null;
  last_seen_at: string | null;
  disabled: boolean;
}

export const usersApi = {
  myFamily: () => api<Family>('/v1/families/me'),
  createFamily: (name: string) => api<Family>('/v1/families', { method: 'POST', body: { name } }),
  listChildren: () => api<Child[]>('/v1/children'),
  ageProfiles: () => api<AgeProfile[]>('/v1/children/age-profiles'),
  createChild: (input: ChildCreateInput) =>
    api<Child>('/v1/children', { method: 'POST', body: input }),
  updateChild: (id: string, input: ChildUpdateInput) =>
    api<Child>(`/v1/children/${id}`, { method: 'PATCH', body: input }),
  deleteChild: (id: string) =>
    api<void>(`/v1/children/${id}`, { method: 'DELETE' }),
  uploadChildAvatar: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api<Child>(`/v1/children/${id}/avatar`, { method: 'POST', body: fd });
  },
  deleteChildAvatar: (id: string) =>
    api<Child>(`/v1/children/${id}/avatar`, { method: 'DELETE' }),
  listDevices: (childId: string) =>
    api<Device[]>(`/v1/children/${childId}/devices`),
  unpairDevice: (childId: string, deviceId: string) =>
    api<void>(`/v1/children/${childId}/devices/${deviceId}`, { method: 'DELETE' }),
  generatePairing: (childId: string) =>
    api<PairingCode>(`/v1/children/${childId}/pairing`, { method: 'POST' }),
};
